// import { useSelector } from "react-redux";

import { normalizeUudServerUrl } from "./normalizeUudServerUrl";

/**
 * Default UUD server: PocketBase + legacy HTTP API (`/items`, `/auth/*`, etc.).
 * Override with `REACT_APP_BACKEND_URL` (production) or `REACT_APP_LEGACY_AUTH_URL`.
 *
 * In development, use `""` (same-origin) when the backend is unset or the default public host,
 * so traffic goes through `setupProxy.js` and avoids browser TLS issues to pb02. If you run
 * PocketBase locally, set `REACT_APP_BACKEND_URL` to that origin (e.g. http://127.0.0.1:8090).
 */
export const DEFAULT_UUD_SERVER_URL = "https://pb02.uud.io";

/** When true, production uses `window.location.origin` for API URLs (expects reverse proxy like docker/nginx.conf). */
function pbSameOriginFromEnv(env) {
  return (
    env.REACT_APP_PB_SAME_ORIGIN === "true" ||
    env.REACT_APP_PB_SAME_ORIGIN === "1"
  );
}

function browserOriginForConfig() {
  if (typeof window === "undefined" || !window.location?.origin) return "";
  return String(window.location.origin).replace(/\/$/, "");
}

function isDefaultPublicHost(url) {
  const s = normalizeUudServerUrl(String(url || "").trim().replace(/\/$/, ""));
  return (
    s === "" ||
    s === DEFAULT_UUD_SERVER_URL ||
    s.startsWith("https://pb02.uud.io") ||
    s.startsWith("https://pb1.uud.io")
  );
}

export const BACKEND_URL = (() => {
  if (
    process.env.NODE_ENV === "development" &&
    isDefaultPublicHost(process.env.REACT_APP_BACKEND_URL)
  ) {
    return "";
  }
  const explicit = String(process.env.REACT_APP_BACKEND_URL || "").trim();
  if (explicit) return explicit.replace(/\/$/, "");
  if (
    process.env.NODE_ENV !== "development" &&
    pbSameOriginFromEnv(process.env)
  ) {
    const o = browserOriginForConfig();
    if (o) return o;
  }
  return DEFAULT_UUD_SERVER_URL;
})();

const DEFAULT_LEGACY_AUTH_HOST = DEFAULT_UUD_SERVER_URL;

/**
 * If legacy auth URL points at an old host (pb1 / pb02), rewrite to the current default.
 */
export function sanitizeLegacyAuthBase(base) {
  if (base === "") return "";
  const trimmed = String(base).replace(/\/$/, "");
  try {
    const withProto = trimmed.includes("://") ? trimmed : `https://${trimmed}`;
    const u = new URL(withProto);
    const h = u.hostname.toLowerCase();
    if (h === "pb02.uud.io" || h === "pb1.uud.io" || h === "pb2.uud.io") {
      return DEFAULT_LEGACY_AUTH_HOST;
    }
  } catch {
    return DEFAULT_LEGACY_AUTH_HOST;
  }
  return trimmed;
}

/**
 * Pure resolver for tests and tooling. `getLegacyAuthBaseUrl()` uses `process.env`.
 * @param {NodeJS.ProcessEnv} [env]
 */
export function resolveLegacyAuthBaseUrl(env = process.env) {
  const explicit =
    env.REACT_APP_LEGACY_AUTH_URL ?? env.REACT_APP_AUTH_API_URL;
  if (explicit != null && String(explicit).trim() !== "") {
    return sanitizeLegacyAuthBase(String(explicit).replace(/\/$/, ""));
  }
  if (env.NODE_ENV === "development") {
    return "";
  }
  if (pbSameOriginFromEnv(env)) {
    const o = browserOriginForConfig();
    if (o) return o;
  }
  return DEFAULT_LEGACY_AUTH_HOST;
}

export function getLegacyAuthBaseUrl() {
  return resolveLegacyAuthBaseUrl(process.env);
}

/**
 * Base URL for custom PocketBase routes (`/signup-request/*`).
 * Production uud.io nginx returns 405 for those paths (not proxied), so when
 * BACKEND_URL is same-origin on uud.io, call PocketBase (pb02) directly.
 * In local development, empty backend keeps same-origin + setupProxy.
 */
export function resolveSignupRequestBaseUrl(
  backendUrl,
  pageOrigin,
  nodeEnv = process.env.NODE_ENV
) {
  const backend = String(backendUrl || "").replace(/\/$/, "");
  const origin = String(pageOrigin || "").replace(/\/$/, "");
  if (!backend) {
    if (nodeEnv === "development") return "";
    return DEFAULT_UUD_SERVER_URL;
  }
  if (backend === origin) return DEFAULT_UUD_SERVER_URL;
  try {
    const host = new URL(
      /^https?:\/\//i.test(backend) ? backend : `https://${backend}`
    ).hostname.toLowerCase();
    if (host === "uud.io" || host === "www.uud.io") {
      return DEFAULT_UUD_SERVER_URL;
    }
  } catch {
    return DEFAULT_UUD_SERVER_URL;
  }
  return backend;
}

export function getSignupRequestBaseUrl() {
  return resolveSignupRequestBaseUrl(BACKEND_URL, browserOriginForConfig());
}
// const lang = useSelector((state) => state.lang);
// structure layout
